import { useState, useEffect } from "react";
import {
  fetchHistoriqueMedical,
  type Period,
  type HistoriqueMedicalData,
} from "@/api/patientStatistiques";
import { DonutChart } from "./stats/DonutChart";
import { HorizontalBar } from "./stats/HorizontalBar";

// ─── Constantes ───────────────────────────────────────────────────────────────

const PERIOD_OPTIONS: Array<{ value: Period; label: string }> = [
  { value: "30j", label: "30 derniers jours" },
  { value: "60j", label: "60 derniers jours" },
  { value: "90j", label: "90 derniers jours" },
  { value: "6m", label: "6 derniers mois" },
  { value: "9m", label: "9 derniers mois" },
  { value: "12m", label: "12 derniers mois" },
  { value: "1a", label: "1 an" },
];

const TYPE_CONSULT_LABELS: Record<string, string> = {
  consultation_generale: "Générale",
  consultation_specialiste: "Spécialiste",
  consultation_urgence: "Urgence",
  consultation_telemedecine: "Télémédecine",
  consultation_chirurgie: "Chirurgie",
  consultation_maternite: "Maternité",
  consultation_pediatrie: "Pédiatrie",
  consultation_psychiatrie: "Psychiatrie",
};

const ETAT_LABELS: Record<string, string> = {
  gueri: "Guéri",
  en_traitement: "En traitement",
  sorti_sans_avis_medical: "Sorti sans avis",
  decede: "Décédé",
  sans_etat: "Sans état",
};

const ETAT_COLORS = ["#22c55e", "#00553E", "#f97316", "#ef4444", "#94a3b8"];

const DOSSIER_STATUT_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  actif: { label: "Actif", color: "#15803d", bg: "#dcfce7" },
  en_attente: { label: "En attente", color: "#92400e", bg: "#fef3c7" },
  a_verifier: { label: "À vérifier", color: "#9a3412", bg: "#ffedd5" },
  archive: { label: "Archivé", color: "#475569", bg: "#f1f5f9" },
};

const ETAT_SORTIE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  gueri: { label: "Guéri", color: "#15803d", bg: "#dcfce7" },
  en_traitement: { label: "En traitement", color: "#1e40af", bg: "#dbeafe" },
  sorti_sans_avis_medical: { label: "Sorti sans avis", color: "#9a3412", bg: "#ffedd5" },
  decede: { label: "Décédé", color: "#991b1b", bg: "#fee2e2" },
  sans_etat: { label: "Sans état", color: "#475569", bg: "#f1f5f9" },
};

// ─── Sous-composants ──────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  color = "default",
  subtitle,
}: {
  label: string;
  value: string | number;
  color?: "default" | "green" | "orange" | "red" | "blue";
  subtitle?: string;
}) {
  const valueClass =
    color === "green"
      ? "text-green-700"
      : color === "orange"
        ? "text-orange-500"
        : color === "red"
          ? "text-red-600"
          : color === "blue"
            ? "text-blue-600"
            : "text-[#0B1F16]";

  return (
    <div className="rounded-[10px] border bg-white p-4" style={{ borderColor: "#E5E7EB" }}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${valueClass}`}>{value}</p>
      {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-4 text-base font-semibold text-[#0B1F16]">{children}</h2>
  );
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#00553E] border-t-transparent" />
    </div>
  );
}

function Badge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span
      className="inline-block rounded-full px-2.5 py-0.5 text-xs font-medium"
      style={{ color, backgroundColor: bg }}
    >
      {label}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function MonEspaceSante() {
  const [period, setPeriod] = useState<Period>("30j");
  const [data, setData] = useState<HistoriqueMedicalData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchHistoriqueMedical(period).then((res) => {
      if (res.ok && res.data?.consultations != null) setData(res.data);
      else setData(null);
      setLoading(false);
    });
  }, [period]);

  const periodLabel =
    PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? period;

  // Données pour DonutChart états de sortie
  const etatsDonutData = data?.consultations?.etatsSortie
    ? Object.entries(data.consultations.etatsSortie)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({
          name: ETAT_LABELS[k] ?? k,
          value: v,
        }))
    : [];

  const etatsDonutColors = data?.consultations?.etatsSortie
    ? Object.keys(data.consultations.etatsSortie)
        .filter((k) => (data.consultations.etatsSortie as Record<string, number>)[k] > 0)
        .map(
          (k) =>
            ETAT_COLORS[
              ["gueri", "en_traitement", "sorti_sans_avis_medical", "decede", "sans_etat"].indexOf(k)
            ] ?? "#94a3b8"
        )
    : [];

  // Données pour HorizontalBar types de consultations
  const parTypeBarData = data?.consultations?.parType
    ? Object.entries(data.consultations.parType)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({
          name: TYPE_CONSULT_LABELS[k] ?? k,
          value: v,
        }))
    : [];

  const derniere = data?.consultations?.derniereConsultation ?? null;
  const dossier = data?.dossierMedical ?? null;
  const dossierConfig = dossier?.statut
    ? (DOSSIER_STATUT_CONFIG[dossier.statut] ?? { label: dossier.statut, color: "#475569", bg: "#f1f5f9" })
    : null;
  const derniereEtatConfig = derniere?.etatSortie
    ? (ETAT_SORTIE_CONFIG[derniere.etatSortie] ?? { label: derniere.etatSortie, color: "#475569", bg: "#f1f5f9" })
    : null;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-[12px] border bg-white px-5 py-4" style={{ borderColor: "#E5E7EB" }}>
        <div>
          <h1 className="text-lg font-bold text-[#0B1F16]">Mon espace santé</h1>
          <p className="text-sm text-gray-500">Aperçu de votre historique médical personnel</p>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          className="self-start rounded-[8px] border px-3 py-2 text-sm text-[#0B1F16] focus:outline-none focus:ring-2 focus:ring-[#00553E]"
          style={{ borderColor: "#E5E7EB" }}
        >
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <Spinner />
      ) : !data ? (
        <SectionCard>
          <p className="text-center text-sm text-gray-500">Données indisponibles.</p>
        </SectionCard>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label="Consultations (total)"
              value={data.consultations.total}
              subtitle="Historique complet"
            />
            <StatCard
              label="En cours"
              value={data.consultations.enCours}
              color={data.consultations.enCours > 0 ? "orange" : "default"}
              subtitle="Non clôturées"
            />
            <StatCard
              label="Ordonnances"
              value={data.ordonnances.total}
              color="blue"
              subtitle={periodLabel}
            />
            <StatCard
              label="Vaccinations"
              value={data.vaccinations.total}
              color="green"
              subtitle="Historique complet"
            />
          </div>

          {/* Dernière consultation + Dossier médical */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SectionCard>
              <SectionTitle>Dernière consultation</SectionTitle>
              {derniere ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Date</span>
                    <span className="font-medium text-[#0B1F16]">{formatDate(derniere.date)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Type</span>
                    <span className="font-medium text-[#0B1F16]">
                      {TYPE_CONSULT_LABELS[derniere.typeConsultation] ?? derniere.typeConsultation}
                    </span>
                  </div>
                  {derniere.etatSortie && derniereEtatConfig && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">État de sortie</span>
                      <Badge
                        label={derniereEtatConfig.label}
                        color={derniereEtatConfig.color}
                        bg={derniereEtatConfig.bg}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Aucune consultation enregistrée.</p>
              )}
            </SectionCard>

            <SectionCard>
              <SectionTitle>Dossier médical</SectionTitle>
              {dossier?.present ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Numéro</span>
                    <span className="font-medium font-mono text-[#0B1F16]">
                      {dossier.numeroDossier ?? "—"}
                    </span>
                  </div>
                  {dossierConfig && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Statut</span>
                      <Badge
                        label={dossierConfig.label}
                        color={dossierConfig.color}
                        bg={dossierConfig.bg}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Aucun dossier médical associé.</p>
              )}
            </SectionCard>
          </div>

          {/* États de sortie */}
          {etatsDonutData.length > 0 && (
            <SectionCard>
              <SectionTitle>États de sortie (historique)</SectionTitle>
              <DonutChart data={etatsDonutData} colors={etatsDonutColors} height={240} />
            </SectionCard>
          )}

          {/* Types de consultations */}
          {parTypeBarData.length > 0 && (
            <SectionCard>
              <SectionTitle>Types de consultations — {periodLabel}</SectionTitle>
              <HorizontalBar data={parTypeBarData} />
            </SectionCard>
          )}

          {/* Analyses */}
          <SectionCard>
            <SectionTitle>Analyses</SectionTitle>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Prescrites ({periodLabel})</span>
                <span className="font-bold text-[#0B1F16]">{data.analyses.total}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Avec résultats (historique)</span>
                <span className="font-bold text-green-700">{data.analyses.avecResultats}</span>
              </div>
              {data.analyses.total > 0 && (
                <div>
                  <div className="mb-1 flex justify-between text-xs text-gray-400">
                    <span>Résultats disponibles</span>
                    <span>
                      {Math.round((data.analyses.avecResultats / Math.max(data.analyses.total, 1)) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-[#00553E]"
                      style={{
                        width: `${Math.round((data.analyses.avecResultats / Math.max(data.analyses.total, 1)) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
